/** Adempimenti seguiti dallo scadenzario (E8 del backlog). */
export const TIPI_SCADENZA = [
  'taratura_contatori',
  'taratura_spi',
  'arera_indagine',
  'arera_unbundling',
  'arera_contributo',
  'antimafia',
  'fuel_mix',
  'diritto_licenza',
  'vidimazione_registri',
  'adeguamento_cci',
  'personalizzata',
] as const
export type TipoScadenza = (typeof TIPI_SCADENZA)[number]

export const ETICHETTA_SCADENZA: Record<TipoScadenza, string> = {
  taratura_contatori: 'Taratura contatori',
  taratura_spi: 'Taratura SPI (sistema di protezione d interfaccia)',
  arera_indagine: 'ARERA — Indagine annuale',
  arera_unbundling: 'ARERA — Unbundling (separazione contabile)',
  arera_contributo: 'ARERA — Contributo di funzionamento',
  antimafia: 'Antimafia (pratica GSE)',
  fuel_mix: 'Comunicazione Fuel Mix GSE',
  diritto_licenza: 'Versamento diritto annuale di licenza',
  vidimazione_registri: 'Vidimazione registri di produzione',
  adeguamento_cci: 'Adeguamento CCI',
  personalizzata: 'Scadenza personalizzata',
}

export type EnteScadenza = 'DSO' | 'ADM' | 'GSE' | 'ARERA' | 'Altro'

/** L'ente a cui si risponde: il cruscotto dell'impianto raggruppa gli adempimenti cosi'. */
export const ENTE_SCADENZA: Record<TipoScadenza, EnteScadenza> = {
  taratura_spi: 'DSO',
  taratura_contatori: 'ADM',
  antimafia: 'GSE',
  fuel_mix: 'GSE',
  arera_indagine: 'ARERA',
  arera_unbundling: 'ARERA',
  arera_contributo: 'ARERA',
  diritto_licenza: 'ADM',
  vidimazione_registri: 'ADM',
  adeguamento_cci: 'DSO',
  personalizzata: 'Altro',
}

export type Ricorrenza = 'da_scadenza' | 'da_rinnovo'

export interface Predefiniti {
  readonly giorniPreavviso: number
  /** Null: una tantum. */
  readonly periodicitaMesi: number | null
  readonly ricorrenza: Ricorrenza | null
}

const UNA_TANTUM = { periodicitaMesi: null, ricorrenza: null } as const

/**
 * Valori proposti nel modulo quando si sceglie l'adempimento: l'operatore li puo'
 * cambiare. Sono le cadenze della scheda Excel dei clienti, non regole di calcolo.
 */
export const PREDEFINITI_SCADENZA: Record<TipoScadenza, Predefiniti> = {
  taratura_spi: { giorniPreavviso: 60, periodicitaMesi: 60, ricorrenza: 'da_rinnovo' },
  taratura_contatori: { giorniPreavviso: 60, periodicitaMesi: 36, ricorrenza: 'da_rinnovo' },
  antimafia: { giorniPreavviso: 60, periodicitaMesi: 12, ricorrenza: 'da_rinnovo' },
  fuel_mix: { giorniPreavviso: 30, periodicitaMesi: 12, ricorrenza: 'da_scadenza' },
  arera_indagine: { giorniPreavviso: 30, periodicitaMesi: 12, ricorrenza: 'da_scadenza' },
  arera_unbundling: { giorniPreavviso: 30, periodicitaMesi: 12, ricorrenza: 'da_scadenza' },
  arera_contributo: { giorniPreavviso: 30, periodicitaMesi: 12, ricorrenza: 'da_scadenza' },
  diritto_licenza: { giorniPreavviso: 30, periodicitaMesi: 12, ricorrenza: 'da_scadenza' },
  vidimazione_registri: { giorniPreavviso: 90, periodicitaMesi: 12, ricorrenza: 'da_scadenza' },
  adeguamento_cci: { giorniPreavviso: 120, ...UNA_TANTUM },
  personalizzata: { giorniPreavviso: 30, ...UNA_TANTUM },
}

export function etichettaPeriodicita(mesi: number | null): string {
  if (mesi === null) return 'Una tantum'
  if (mesi === 12) return 'Annuale'
  if (mesi === 6) return 'Semestrale'
  return mesi % 12 === 0 ? `Ogni ${mesi / 12} anni` : `Ogni ${mesi} mesi`
}

export type StatoScadenza = 'completata' | 'scaduta' | 'in_avvicinamento' | 'futura'

export interface ScadenzaCalcolabile {
  readonly scadenza: string
  readonly giorniPreavviso: number
  readonly completataIl: string | null
}

const GIORNO_MS = 24 * 60 * 60 * 1000
const soloData = (d: Date | string): number => Date.parse(`${new Date(d).toISOString().slice(0, 10)}T00:00:00Z`)

/**
 * Lo stato non sta nel database: dipende da oggi, e memorizzarlo vorrebbe dire
 * ricalcolarlo ogni notte per tutte le scadenze. Lo usano il portale e, quando
 * ci sara', il motore email.
 */
export function statoScadenza(s: ScadenzaCalcolabile, oggi: Date = new Date()): StatoScadenza {
  if (s.completataIl !== null) return 'completata'
  const giorniMancanti = (soloData(s.scadenza) - soloData(oggi)) / GIORNO_MS
  if (giorniMancanti < 0) return 'scaduta'
  return giorniMancanti <= s.giorniPreavviso ? 'in_avvicinamento' : 'futura'
}

/** Un tipo previsto ha un nome fisso; la personalizzata porta il suo. */
export function titoloScadenza(s: { tipo: TipoScadenza; titolo: string | null }): string {
  return s.tipo === 'personalizzata' ? (s.titolo ?? ETICHETTA_SCADENZA.personalizzata) : ETICHETTA_SCADENZA[s.tipo]
}
